import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/hooks/useLanguage';
import { useIsMobile } from '@/hooks/use-mobile';
import { useUserRole } from '@/hooks/useUserRole';
import { useTenant } from '@/hooks/useTenant';
import { useUnreadEmailCount } from '@/hooks/useUnreadEmailCount';
import { hasFeatureAccess } from '@/lib/permissions';
import { getRoleDisplayName } from '@/lib/permissions';
import {
  LayoutDashboard,
  Inbox,
  CheckSquare,
  CalendarDays,
  Video,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Users,
  FolderKanban,
  FileText,
  FileSearch,
  UserCircle,
  Activity,
  ClipboardList,
  Calendar,
  Shield,
  BookOpen,
  Palette,
  User,
  Sparkles,
  Plug,
  Layers,
  Scale,
  PieChart,
  Radio,
  Brain,
  Rocket,
  AlertCircle,
  Gavel,
  History,
  UserCheck,
} from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { DirectionalIcon } from '@/components/layout/DirectionalIcon';
import { Badge } from '@/components/ui/badge';
import type { AppRole } from '@/lib/permissions';

interface SidebarProps {
  activeView: string;
  onViewChange: (view: string) => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

// Items always visible at the top of the nav, ungrouped — the
// day-to-day workspace surfaces, not part of the three macro areas.
const workspaceItems = [
  { id: 'dashboard', labelKey: 'dashboard', icon: LayoutDashboard },
  { id: 'actionHub', labelKey: 'actionHub', icon: CheckSquare },
  { id: 'calendar', labelKey: 'calendar', icon: CalendarDays },
  { id: 'documents', labelKey: 'documents', icon: FileText },
  { id: 'documentIntelligence', labelKey: 'documentIntelligence', icon: FileSearch },
];

// Single flat nav button — HITL review is a dedicated surface (approval
// workflow, review workbench, policy rules, AI kill switch), separate
// from the Governance analysis areas below.
const hitlItem = { id: 'hitlWorkbench', labelKey: 'hitlWorkbench', icon: UserCheck };

// Not yet mapped into any of the three macro areas by the current
// consolidation pass — left exactly where they were, ungrouped.
const otherItems = [
  { id: 'compliance', labelKey: 'compliance', icon: Scale },
  { id: 'activity', labelKey: 'activity', icon: Activity },
  { id: 'aiControls', labelKey: 'aiControls', icon: Sparkles },
  { id: 'branding', labelKey: 'branding', icon: Palette },
  { id: 'tenantSettings', labelKey: 'tenantSettings', icon: Shield },
];

interface GroupMenuItem {
  id: string;
  labelKey: string;
  icon: typeof LayoutDashboard;
  /** When set, clicking navigates to this tenant-relative route instead of switching activeView. */
  route?: string;
}

interface NavGroup {
  key: 'projects' | 'communication' | 'governance' | 'system';
  labelKey: string;
  items: GroupMenuItem[];
}

// The three macro areas (plus System) per the platform simplification
// brief — collapsible sections with a chevron to show/hide their items.
const navGroups: NavGroup[] = [
  {
    key: 'projects',
    labelKey: 'navGroupProjects',
    items: [
      { id: 'projects', labelKey: 'projects', icon: FolderKanban },
      { id: 'portfolio', labelKey: 'portfolio', icon: Layers },
      { id: 'strategy', labelKey: 'strategy', icon: BarChart3 },
    ],
  },
  {
    key: 'communication',
    labelKey: 'navGroupCommunication',
    items: [
      { id: 'inbox', labelKey: 'inbox', icon: Inbox },
      { id: 'stakeholders', labelKey: 'stakeholders', icon: UserCircle },
      { id: 'team', labelKey: 'team', icon: Users },
      { id: 'meetings', labelKey: 'meetings', icon: Video },
      { id: 'reports', labelKey: 'reports', icon: ClipboardList },
      { id: 'weeklyDigest', labelKey: 'weeklyDigest', icon: Calendar },
    ],
  },
  {
    key: 'governance',
    labelKey: 'governance',
    items: [
      { id: 'signals', labelKey: 'signals', icon: Radio },
      { id: 'risks', labelKey: 'risks', icon: Brain },
      { id: 'dependencies', labelKey: 'dependencies', icon: Rocket },
      { id: 'actionsRequired', labelKey: 'actionsRequired', icon: AlertCircle },
      { id: 'decisions', labelKey: 'decisions', icon: Gavel },
      { id: 'auditTrail', labelKey: 'auditTrail', icon: History },
    ],
  },
  {
    key: 'system',
    labelKey: 'navGroupSystem',
    items: [
      { id: 'knowledge', labelKey: 'knowledge', icon: BookOpen },
    ],
  },
];

const SidebarContent = ({
  activeView,
  onViewChange,
  isCollapsed,
  setIsCollapsed,
  onItemClick,
  userRole,
}: {
  activeView: string;
  onViewChange: (view: string) => void;
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  onItemClick?: () => void;
  userRole: AppRole | null;
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { tenantSlug } = useTenant();
  const { resolvedTheme } = useTheme();
  const { count: unreadEmailCount } = useUnreadEmailCount();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    projects: true,
    communication: true,
    governance: true,
    system: true,
  });

  const tenantPath = (suffix: string) =>
    tenantSlug ? `/${tenantSlug}${suffix}` : suffix;

  const handleViewChange = (view: string) => {
    onViewChange(view);
    onItemClick?.();
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    onItemClick?.();
  };

  const handleItemClick = (item: GroupMenuItem) => {
    if (item.route) {
      handleNavigate(tenantPath(item.route));
    } else {
      handleViewChange(item.id);
    }
  };

  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  const showAnalytics = hasFeatureAccess(userRole, 'analytics');
  const visibleWorkspaceItems = workspaceItems.filter((item) => hasFeatureAccess(userRole, item.id));
  const visibleOtherItems = otherItems.filter((item) => hasFeatureAccess(userRole, item.id));
  const showHitl = hasFeatureAccess(userRole, hitlItem.id);
  const visibleGroups = navGroups
    .map((group) => ({ ...group, items: group.items.filter((item) => hasFeatureAccess(userRole, item.id)) }))
    .filter((group) => group.items.length > 0);

  const renderNavButton = (item: GroupMenuItem, isActive: boolean) => {
    const Icon = item.icon;
    return (
      <button
        key={item.id}
        onClick={() => handleItemClick(item)}
        className={cn(
          'masira-sidebar-item w-full',
          isActive && 'masira-sidebar-item-active'
        )}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        {!isCollapsed && (
          <span className="flex-1 text-start">{t(item.labelKey)}</span>
        )}
        {item.id === 'inbox' && unreadEmailCount > 0 && !isCollapsed && (
          <span className="ms-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium">
            {unreadEmailCount > 99 ? '99+' : unreadEmailCount}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      {/* Logo */}
      <div className="h-16 flex items-center px-6 border-b border-border">
        <div className="flex items-center gap-3">
          {isCollapsed ? (
            <img src="/masira-logo-mark.svg" alt="Masira" className="w-8 h-8" />
          ) : (
            <img
              src={resolvedTheme === 'dark' ? '/masira-logo-word-mark-dark-theme.svg' : '/masira-logo-word-mark-light-theme.svg'}
              alt="Masira"
              className="h-8 w-auto masira-fade-in"
            />
          )}
        </div>
      </div>

      {/* User Role Badge */}
      {!isCollapsed && userRole && (
        <div className="px-4 py-2 border-b border-border">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <User className="w-3 h-3" />
            <span>Role:</span>
            <Badge variant="secondary" className="text-xs font-normal">
              {getRoleDisplayName(userRole)}
            </Badge>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto masira-scrollbar">
        {visibleWorkspaceItems.map((item) => renderNavButton(item, activeView === item.id))}

        {visibleGroups.map((group) => {
          const isOpen = openGroups[group.key] ?? true;
          return (
            <div key={group.key} className="pt-2">
              <button
                onClick={() => toggleGroup(group.key)}
                className="flex items-center w-full gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors"
              >
                {!isCollapsed && <span className="flex-1 text-start">{t(group.labelKey)}</span>}
                <ChevronDown
                  className={cn('w-3.5 h-3.5 flex-shrink-0 transition-transform', !isOpen && '-rotate-90')}
                />
              </button>
              {(isOpen || isCollapsed) && (
                <div className="space-y-1 mt-1">
                  {group.items.map((item) => renderNavButton(item, activeView === item.id))}
                </div>
              )}
            </div>
          );
        })}

        {showHitl && (
          <div className="pt-2">
            {renderNavButton(hitlItem, activeView === hitlItem.id)}
          </div>
        )}

        {visibleOtherItems.length > 0 && (
          <div className="pt-2 space-y-1">
            {visibleOtherItems.map((item) => renderNavButton(item, activeView === item.id))}
          </div>
        )}
      </nav>

      {/* Settings & Admin */}
      <div className="p-4 border-t border-border space-y-1">
        <button
          onClick={() => handleNavigate(tenantPath('/integrations'))}
          className={cn(
            'masira-sidebar-item w-full',
            activeView === 'integrations' && 'masira-sidebar-item-active'
          )}
        >
          <Plug className="w-5 h-5 flex-shrink-0" />
          {!isCollapsed && (
            <span className="flex-1 text-start">
              {t('integrations')}
            </span>
          )}
        </button>

        {showAnalytics && (
          <button
            onClick={() => handleNavigate(tenantPath('/admin/analytics'))}
            className="masira-sidebar-item w-full"
          >
            <PieChart className="w-5 h-5 flex-shrink-0" />
            {!isCollapsed && (
              <span className="flex-1 text-start">
                {t('analytics')}
              </span>
            )}
          </button>
        )}

        <button
          onClick={() => handleNavigate(tenantPath('/settings'))}
          className={cn(
            'masira-sidebar-item w-full',
            activeView === 'settings' && 'masira-sidebar-item-active'
          )}
        >
          <Settings className="w-5 h-5 flex-shrink-0" />
          {!isCollapsed && (
            <span className="flex-1 text-start">
              {t('settings')}
            </span>
          )}
        </button>

        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="masira-sidebar-item w-full justify-center hidden md:flex"
        >
          {isCollapsed ? (
            <DirectionalIcon ltr={ChevronRight} rtl={ChevronLeft} className="w-5 h-5" />
          ) : (
            <>
              <DirectionalIcon ltr={ChevronLeft} rtl={ChevronRight} className="w-5 h-5" />
              <span className="flex-1 text-start">
                {t('collapse')}
              </span>
            </>
          )}
        </button>
      </div>
    </>
  );
};

const Sidebar = ({ activeView, onViewChange, isOpen = false, onOpenChange }: SidebarProps) => {
  const { isRTL } = useLanguage();
  const isMobile = useIsMobile();
  const { role } = useUserRole();

  // Mobile: Sheet drawer
  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent 
          side={isRTL ? 'right' : 'left'} 
          className="w-72 p-0 bg-card border-border"
        >
          <SidebarContent
            activeView={activeView}
            onViewChange={onViewChange}
            isCollapsed={false}
            setIsCollapsed={() => {}}
            onItemClick={() => onOpenChange?.(false)}
            userRole={role}
          />
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: Fixed sidebar
  return (
    <aside
      className={cn(
        'fixed top-0 h-screen bg-card border-border flex flex-col transition-all duration-300 z-50 w-72',
        'start-0 border-e'
      )}
    >
      <SidebarContent
        activeView={activeView}
        onViewChange={onViewChange}
        isCollapsed={false}
        setIsCollapsed={() => {}}
        userRole={role}
      />
    </aside>
  );
};

export default Sidebar;